import React, { useState, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, Textarea, FormControl, FormLabel,
  Box, Text, Badge, useToast, Flex, IconButton, Divider,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiSave } from 'react-icons/fi';
import api from '../api';

export default function DebtCollectionModal({ isOpen, onClose }) {
  const [actions, setActions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', days_after_due: '', action_description: '', email_subject: '', email_body: '', sort_order: '' });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const fetchActions = async () => { try { const { data } = await api.get('/debt-collection'); setActions(data); } catch(e) {} };

  useEffect(() => { if (isOpen) { fetchActions(); setEditingId(null); } }, [isOpen]);

  const openEdit = (a) => {
    setForm({ name: a.name, days_after_due: a.days_after_due, action_description: a.action_description || '', email_subject: a.email_subject || '', email_body: a.email_body || '', sort_order: a.sort_order || 0 });
    setEditingId(a.id);
  };

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editingId) await api.put(`/debt-collection/${editingId}`, form);
      else await api.post('/debt-collection', form);
      await fetchActions(); setEditingId(null);
      setForm({ name: '', days_after_due: '', action_description: '', email_subject: '', email_body: '', sort_order: '' });
      toast({ title: 'Saved', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="85vh">
        <ModalHeader fontSize="md">Debt Collection Settings</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="11px" color="gray.500" mb={3}>
            Configure automated debt collection actions with UK-compliant email templates.
            Use placeholders: {'{{invoice_id}}'}, {'{{amount}}'}, {'{{due_date}}'}, {'{{issue_date}}'}, {'{{contact_name}}'}, {'{{company_name}}'}, {'{{late_fee}}'}.
          </Text>

          <Accordion allowMultiple>
            {actions.map(a => (
              <AccordionItem key={a.id} border="1px" borderColor="gray.100" borderRadius="md" mb={2}>
                <AccordionButton py={2} px={3}>
                  <Flex flex={1} align="center" gap={2}>
                    <Badge colorScheme={a.days_after_due <= 7 ? 'green' : a.days_after_due <= 30 ? 'orange' : 'red'} fontSize="9px">
                      Day {a.days_after_due}
                    </Badge>
                    <Text fontSize="12px" fontWeight="600">{a.name}</Text>
                    <Badge fontSize="8px" colorScheme="gray">Step {a.sort_order}</Badge>
                  </Flex>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={3} px={3}>
                  {editingId === a.id ? (
                    <VStack spacing={2}>
                      <HStack w="100%" spacing={2}>
                        <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Action Name</FormLabel>
                          <Input size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></FormControl>
                        <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Days After Due</FormLabel>
                          <Input size="sm" type="number" value={form.days_after_due} onChange={e => setForm(p => ({ ...p, days_after_due: e.target.value }))} /></FormControl>
                        <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Sort Order</FormLabel>
                          <Input size="sm" type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: e.target.value }))} /></FormControl>
                      </HStack>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Action Description</FormLabel>
                        <Input size="sm" value={form.action_description} onChange={e => setForm(p => ({ ...p, action_description: e.target.value }))} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Email Subject</FormLabel>
                        <Input size="sm" value={form.email_subject} onChange={e => setForm(p => ({ ...p, email_subject: e.target.value }))} /></FormControl>
                      <FormControl><FormLabel fontSize="xs" mb={0.5}>Email Body</FormLabel>
                        <Textarea size="sm" rows={8} value={form.email_body} onChange={e => setForm(p => ({ ...p, email_body: e.target.value }))} fontFamily="mono" fontSize="11px" /></FormControl>
                      <HStack spacing={2} alignSelf="flex-end">
                        <Button size="xs" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                        <Button size="xs" colorScheme="blue" leftIcon={<FiSave />} onClick={handleSave} isLoading={saving}>Save</Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <>
                      <Text fontSize="11px" color="gray.600" mb={1}>{a.action_description}</Text>
                      {a.email_subject && (
                        <Box bg="gray.50" p={2} borderRadius="md" mt={1}>
                          <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase">Email Subject</Text>
                          <Text fontSize="11px" fontWeight="500">{a.email_subject}</Text>
                          <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase" mt={1}>Email Body</Text>
                          <Text fontSize="10px" whiteSpace="pre-wrap" color="gray.600" fontFamily="mono">{a.email_body}</Text>
                        </Box>
                      )}
                      <HStack mt={2} spacing={1}>
                        <Button size="xs" variant="ghost" leftIcon={<FiEdit2 />} onClick={() => openEdit(a)}>Edit</Button>
                        <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<FiTrash2 />}
                          onClick={async () => { await api.delete(`/debt-collection/${a.id}`); fetchActions(); }}>Delete</Button>
                      </HStack>
                    </>
                  )}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          <Divider my={3} />
          <Box bg="blue.50" p={3} borderRadius="md">
            <Text fontSize="11px" fontWeight="600" mb={2}>Add New Action</Text>
            <VStack spacing={2}>
              <HStack w="100%" spacing={2}>
                <FormControl flex={2}><FormLabel fontSize="xs" mb={0.5}>Name</FormLabel>
                  <Input size="sm" value={editingId ? '' : form.name} onChange={e => { if (!editingId) setForm(p => ({ ...p, name: e.target.value })); }} /></FormControl>
                <FormControl flex={1}><FormLabel fontSize="xs" mb={0.5}>Days After Due</FormLabel>
                  <Input size="sm" type="number" value={editingId ? '' : form.days_after_due} onChange={e => { if (!editingId) setForm(p => ({ ...p, days_after_due: e.target.value })); }} /></FormControl>
              </HStack>
              <Button size="xs" colorScheme="blue" leftIcon={<FiPlus />} onClick={() => { if (!editingId) handleSave(); }} alignSelf="flex-end">Add Action</Button>
            </VStack>
          </Box>
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>
  );
}
